import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Sparkles, Loader2 } from 'lucide-react';

/**
 * Screenshot-based dashboard tour.
 *
 * A simple, robust, DOM-independent carousel: for each step we show the
 * captured screenshot of the module PLUS a title / caption underneath.
 * No spotlight positioning, no sidebar-auto-open dance, no clipping —
 * just a modal with clear Next / Back / progress.
 *
 * `steps` = array of { title, caption, image } (image is a full URL).
 */
export function CarouselTour({ steps, open, onClose, onFinish, storageKey }) {
  const [idx, setIdx] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    if (open) { setIdx(0); setImgLoaded(false); }
  }, [open]);

  useEffect(() => { setImgLoaded(false); }, [idx]);

  const goNext = useCallback(() => {
    if (idx < steps.length - 1) setIdx(idx + 1);
    else {
      if (storageKey) localStorage.setItem(storageKey, 'done');
      onFinish?.();
      onClose?.();
    }
  }, [idx, steps.length, storageKey, onFinish, onClose]);

  const goPrev = () => setIdx(Math.max(0, idx - 1));
  const doSkip = () => {
    if (storageKey) localStorage.setItem(storageKey, 'skipped');
    onClose?.();
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') doSkip();
      else if (e.key === 'Enter' || e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, idx, goNext]);

  if (!open || !steps || steps.length === 0) return null;
  const step = steps[idx];

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6" data-testid="dashboard-tour">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={doSkip} />

      {/* Sheet */}
      <div className="relative w-full max-w-3xl max-h-full flex flex-col rounded-2xl bg-[#041A3B] border border-[#00E5FF]/30 shadow-2xl shadow-[#00E5FF]/20 text-white keep-dark overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2 text-[#00E5FF] font-mono text-[10px] sm:text-[11px] tracking-[0.25em] uppercase">
            <Sparkles className="w-3.5 h-3.5" />
            Step {idx + 1} of {steps.length}
          </div>
          <button
            onClick={doSkip}
            className="text-[#A0B4CC] hover:text-white transition-colors p-1 -mr-1"
            aria-label="Close tour"
            data-testid="tour-skip-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Screenshot */}
        <div className="relative bg-[#000B18] flex items-center justify-center overflow-hidden" style={{ minHeight: 180 }}>
          {!imgLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" />
            </div>
          )}
          <img
            src={step.image}
            alt={step.title}
            onLoad={() => setImgLoaded(true)}
            className={`w-full h-auto max-h-[52vh] object-contain transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            data-testid="tour-screenshot"
          />
        </div>

        {/* Caption */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-t border-white/10 flex-shrink-0">
          <h3 className="font-outfit text-lg sm:text-xl font-semibold text-white leading-tight mb-1.5" data-testid="tour-title">
            {step.title}
          </h3>
          <p className="text-[#A0B4CC] text-sm leading-relaxed">{step.caption}</p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-t border-white/10 bg-[#000B18]/50 flex-shrink-0">
          <button
            onClick={goPrev}
            disabled={idx === 0}
            className="inline-flex items-center gap-1 text-[#A0B4CC] hover:text-white text-sm disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0 px-2 py-1"
            data-testid="tour-prev-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          {/* Compact progress bar */}
          <div className="flex-1 min-w-0 flex items-center gap-2 px-2">
            <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#00E5FF] to-[#FFB800] transition-all duration-300"
                style={{ width: `${((idx + 1) / steps.length) * 100}%` }}
              />
            </div>
            <span className="font-mono text-[10px] text-[#5C7599] tabular-nums flex-shrink-0">
              {idx + 1}/{steps.length}
            </span>
          </div>

          <button
            onClick={goNext}
            className="inline-flex items-center gap-1 btn-primary text-sm px-4 py-2 rounded-lg flex-shrink-0"
            data-testid="tour-next-btn"
          >
            {idx === steps.length - 1 ? 'Got it' : 'Next'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function tourStorageKey(userId, role) {
  return `aga.tour.v3.${role || 'guest'}.${userId || 'anon'}`;
}
